/* global debounce */

if (!customElements.get('predictive-search')) {
  const HISTORY_STORAGE_KEY = 'predictiveSearchHistory';

  class PredictiveSearchHistory {
    constructor(maxItems = 4) {
      this.maxItems = maxItems;
      this.storageKey = HISTORY_STORAGE_KEY;
      this.container = null;
      this.wrapper = null;
      this.clearBtn = null;
      this.removeIcon = null;
      this.searchUrl = '/search';
      this.containerHandler = null;
      this.clearHandler = null;
    }

    read() {
      try {
        const parsed = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    persist(history) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(history.slice(0, this.maxItems)));
      } catch (error) {
        // Ignore write errors (e.g., private mode)
      }
    }

    render(history = this.read()) {
      if (!this.container || !this.wrapper) return;

      this.container.innerHTML = '';

      if (!history.length) {
        this.wrapper.classList.add('is-empty');
        if (this.clearBtn) this.clearBtn.setAttribute('hidden', 'hidden');
        return;
      }

      this.wrapper.classList.remove('is-empty');
      if (this.clearBtn) this.clearBtn.removeAttribute('hidden');

      history.slice(0, this.maxItems).forEach((term) => {
        const itemEl = document.createElement('div');
        itemEl.className = 'predictive--search__prev_item swiper-slide';
        itemEl.dataset.searchTerm = term;

        const text = document.createElement('span');
        text.textContent = term;
        itemEl.appendChild(text);

        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'predictive--search__prev_remove';
        removeBtn.setAttribute('data-prev-search-remove', '');
        removeBtn.setAttribute('aria-label', window.theme?.translations?.reset || 'Remove');
        removeBtn.innerHTML = this.removeIcon || '×';
        itemEl.appendChild(removeBtn);

        this.container.appendChild(itemEl);
      });
    }

    add(term) {
      const query = (term || '').trim();
      if (!query) return;

      const lowerQuery = query.toLowerCase();
      let history = this.read().filter((item) => item.toLowerCase() !== lowerQuery);
      history.unshift(query);
      history = history.slice(0, this.maxItems);

      this.persist(history);
      this.render(history);
    }

    remove(term) {
      const history = this.read().filter((item) => item !== term);
      this.persist(history);
      this.render(history);
    }

    clear() {
      try {
        localStorage.removeItem(this.storageKey);
      } catch (error) {
        // Ignore
      }
      this.render([]);
    }

    unmount() {
      if (this.container && this.containerHandler) {
        this.container.removeEventListener('click', this.containerHandler);
      }
      if (this.clearBtn && this.clearHandler) {
        this.clearBtn.removeEventListener('click', this.clearHandler);
      }
    }

    mountFrom(root) {
      if (!root) return;

      const container = root.querySelector('[data-prev-search-items]');
      const wrapper = container ? container.closest('[data-prev-search]') : null;
      const clearBtn = root.querySelector('[data-prev-search-clear]');
      const removeIconTemplate = root.querySelector('[data-prev-search-remove-icon]');

      this.unmount();

      this.container = container || null;
      this.wrapper = wrapper || null;
      this.clearBtn = clearBtn || null;
      this.searchUrl = wrapper?.dataset.searchUrl
        || window.theme?.routes?.searchUrl
        || '/search';
      this.removeIcon = removeIconTemplate ? removeIconTemplate.innerHTML.trim() : null;

      if (!this.container || !this.wrapper) return;

      this.containerHandler = (evt) => {
        const removeBtn = evt.target.closest('[data-prev-search-remove]');
        if (removeBtn) {
          const term = removeBtn.closest('.predictive--search__prev_item')?.dataset.searchTerm;
          if (term) this.remove(term);
          return;
        }

        const item = evt.target.closest('.predictive--search__prev_item');
        if (item?.dataset.searchTerm) {
          window.location.href = `${this.searchUrl}?q=${encodeURIComponent(item.dataset.searchTerm)}`;
        }
      };

      this.container.addEventListener('click', this.containerHandler);

      this.clearHandler = () => this.clear();
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', this.clearHandler);
      }

      this.render();
    }
  }

  function enhancePredictiveSwipers(root) {
    if (!root) return;
    const containers = root.querySelectorAll('.predictive-search__carousel.swiper');
    const prevSearchContainers = root.querySelectorAll('.predictive--search__prev_swiper.swiper');

    const mobileItemsPerRow = (window.theme?.settings?.mobileSearchItemsPerRow || '1') === '2' ? 2 : 1;
    const isMobileView = window.innerWidth < 769;

    containers.forEach((container) => {
      if (typeof Swiper === 'undefined') return;

      const prev = container.querySelector('.swiper-button-prev');
      const next = container.querySelector('.swiper-button-next');
      const pagination = container.querySelector('.swiper-pagination');
      const isProductsCarousel = container.id === 'header-search-products-carousel';

      // Resolve the layout mode for the current viewport. The products carousel
      // renders differently across the mobile/desktop boundary (and per column
      // setting), so we track the active mode and rebuild when it changes —
      // otherwise a resize leaves a stale Swiper instance or grid class behind.
      let mode = 'desktop';
      if (isProductsCarousel && isMobileView) {
        mode = mobileItemsPerRow === 1 ? 'mobile-1col' : 'mobile-2col';
      }

      // Already built in the correct mode for this viewport: nothing to do.
      if (container.dataset.swiperInitialized === 'true' && container.dataset.swiperMode === mode) {
        return;
      }

      // Mode changed (e.g. resize across the breakpoint): tear down the previous
      // layout so a stale Swiper instance or grid class never lingers.
      if (container.swiper) {
        container.swiper.destroy(true, true);
      }
      container.classList.remove('predictive-search__carousel--grid');

      // Products carousel on mobile: initialize per layout mode
      if (mode === 'mobile-1col') {
        // Vertical free-scroll: natural card heights, ~1.5 cards visible
        // eslint-disable-next-line no-new
        new Swiper(container, {
          direction: 'vertical',
          slidesPerView: 'auto',
          spaceBetween: 12,
          freeMode: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
        });
        container.dataset.swiperInitialized = 'true';
        container.dataset.swiperMode = mode;
        return;
      }

      if (mode === 'mobile-2col') {
        // 2-per-row: 2 columns side by side, scrolling vertically. Swiper's Grid
        // module only supports horizontal direction, so it can't lay slides into
        // columns on a vertical swiper. Instead we skip Swiper here and let the
        // stylesheet render the cards as a native 2-column CSS grid that scrolls
        // vertically (cards keep their full, natural height).
        container.classList.add('predictive-search__carousel--grid');
        container.dataset.swiperInitialized = 'true';
        container.dataset.swiperMode = mode;
        return;
      }

      // eslint-disable-next-line no-new
      new Swiper(container, {
        direction: 'horizontal',
        slidesPerView: 2,
        spaceBetween: 16,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        autoHeight: isProductsCarousel,
        navigation: {
          prevEl: prev,
          nextEl: next,
        },
        pagination: pagination
          ? {
            el: pagination,
            clickable: true,
          }
          : undefined,
        breakpoints: {
          0: isProductsCarousel ? {
            direction: 'horizontal',
            slidesPerView: 2,
            spaceBetween: 12,
            autoHeight: false,
          } : undefined,
          640: isProductsCarousel
            ? {
              direction: 'horizontal',
              slidesPerView: 2,
              spaceBetween: 12,
              autoHeight: false,
            }
            : { slidesPerView: 2, spaceBetween: 10 },
          769: { direction: 'horizontal', slidesPerView: 2, spaceBetween: 10 },
          1380: { slidesPerView: 3, spaceBetween: 10 }
        },
      });

      container.dataset.swiperInitialized = 'true';
      container.dataset.swiperMode = mode;
    });

    prevSearchContainers.forEach((container) => {
      if (typeof Swiper === 'undefined') return;
      const shouldUseSwiper = window.innerWidth > 1170;

      if (!shouldUseSwiper) {
        if (container.swiper) {
          container.swiper.destroy(true, true);
        }
        container.dataset.swiperInitialized = 'false';
        return;
      }

      if (container.dataset.swiperInitialized === 'true') return;

      // eslint-disable-next-line no-new
      new Swiper(container, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        freeMode: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      });

      container.dataset.swiperInitialized = 'true';
    });

    // Retry shortly in case Swiper script was still loading.
    if (typeof Swiper === 'undefined') {
      setTimeout(() => enhancePredictiveSwipers(root), 150);
    }
  }

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.history = new PredictiveSearchHistory();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.form = this.querySelector('.search');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.magnifyIcon = this.querySelector('.search-magnify-icon');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.resizeHandler = debounce(() => enhancePredictiveSwipers(this.results));
      this.addListeners();
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      window.removeEventListener('resize', this.resizeHandler);
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.form.addEventListener('submit', () => this.history.add(this.getQuery()));
      }
      if (this.magnifyIcon) {
        this.magnifyIcon.addEventListener('click', this.handleIconClick.bind(this));
      }
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Returns a fallback term to fetch initial suggestions.
     * @returns {string}
     */
    getDefaultSearchTerm() {
      return (
        this.input.dataset.placeholderOne
        || this.input.placeholder
        || 'a'
      );
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (!this.getQuery().length) {
        evt.preventDefault();
      }
    }

    /**
     * Submits the search form with the current query.
     * @param {boolean} [focus=true] - Whether to focus the input before submitting.
     */
    submitSearch(focus = true) {
      if (focus) this.input.focus();
      if (!this.getQuery().length || !this.form) return;

      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.getResults(this.getDefaultSearchTerm());
        this.resetBtn.hidden = true;
        return;
      }

      this.resetBtn.hidden = false;
      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery() || this.getDefaultSearchTerm();
      this.open();
      this.getResults(searchTerm);
    }

    /**
     * Handles clicks on the magnify icon to open search immediately.
     */
    handleIconClick() {
      this.input.focus();
      this.open();

      const searchTerm = this.getQuery() || this.getDefaultSearchTerm();

      if (this.getQuery().length) {
        this.submitSearch(false);
        return;
      }

      if (this.getAttribute('results') !== 'true') {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          if (evt.target !== this.input) return;
          evt.preventDefault();
          this.submitSearch(false);
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, fetch default suggestions.
      if (!this.getQuery().length) this.getResults(this.getDefaultSearchTerm());
    }

    /**
     * Closes predictive search when clicking outside the component.
     * @param {object} evt - Event object.
     */
    handleOutsideClick(evt) {
      if (!this.hasAttribute('open') || this.contains(evt.target)) return;
      this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.history.mountFrom(this.results);
      enhancePredictiveSwipers(this.results);
      this.setAttribute('results', true);
      this.open();
      this.addEventListener('tabchange', () => enhancePredictiveSwipers(this.results), { once: true });
    }

    /**
     * Clears predictive search results while keeping the overlay visible.
     */
    clearResults() {
      this.results.innerHTML = '';
      this.removeAttribute('results');
      this.input.setAttribute('aria-activedescendant', '');
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      this.resetBtn.hidden = false;
      this.resetBtn.removeAttribute('hidden');
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);
      this.outsideClickHandler = this.outsideClickHandler || this.handleOutsideClick.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
      document.addEventListener('pointerdown', this.outsideClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.blur();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
      document.removeEventListener('pointerdown', this.outsideClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
